/**
 * Help Center Research Page v3
 * 
 * Research-first interface for exploring ThreadLock functionality and external legal information.
 * 
 * KEY PRINCIPLES:
 * - This is a research hub, not a FAQ list
 * - AI is primary, not a bolt-on
 * - Internal (Truth-bound) vs External knowledge is clearly distinguished
 * - No hardcoded content that can drift from reality
 * - Visual consistency with main app
 * 
 * ARCHITECTURE:
 * - Uses useTruthData() for authoritative app knowledge
 * - AI Assistant with mode parameter (internal/external/hybrid)
 * - Role + route aware context
 * - No local footer (uses app-level footer)
 */

import { useState, useMemo, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Search, Sparkles, BookOpen, Globe, HelpCircle, Info } from 'lucide-react';
import { useTruthData } from '@/hooks/useTruthData';
import { AiResearchAssistant } from '@/components/help/AiResearchAssistant';
import { ContactSupportForm } from '@/components/support/ContactSupportForm';

export default function HelpCenterResearchPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    loading: truthLoading,
    activeRole,
    currentRoute,
    routeCapabilities,
    coreCapabilities,
  } = useTruthData();

  const [searchQuery, setSearchQuery] = useState('');
  const [researchMode, setResearchMode] = useState<'internal' | 'external' | 'hybrid'>('hybrid');

  // Initialize from URL parameters (from context help "Research this further")
  useEffect(() => {
    const queryParam = searchParams.get('q');
    const modeParam = searchParams.get('mode') as 'internal' | 'external' | 'hybrid' | null;
    
    if (queryParam) {
      setSearchQuery(queryParam);
    }
    
    if (modeParam && ['internal', 'external', 'hybrid'].includes(modeParam)) {
      setResearchMode(modeParam);
    }
  }, [searchParams]);

  // Generate contextual placeholder based on role and route
  const placeholder = useMemo(() => {
    if (currentRoute) {
      return `Ask about this page: "${currentRoute.component}" or any ThreadLock feature...`;
    }
    
    const examples: Record<string, string> = {
      srl: 'How do I add evidence to my case?',
      pro: 'How does the review queue work?',
      admin: 'How do I verify a professional account?',
    };
    
    return examples[activeRole] || 'How can I help you today?';
  }, [currentRoute, activeRole]);

  // Role label for display
  const roleLabel: Record<string, string> = {
    srl: 'Self-Represented Litigant',
    pro: 'Professional',
    admin: 'Administrator',
  };

  // Quick action prompts based on context
  const quickPrompts = useMemo(() => {
    const base: Array<{ text: string; mode: 'internal' | 'external' | 'hybrid' }> = [
      { text: 'How does ThreadLock work?', mode: 'internal' },
      { text: 'What features are available to me?', mode: 'internal' },
    ];

    if (currentRoute) {
      base.unshift({
        text: `What does this page do?`,
        mode: 'internal',
      });
    }

    if (activeRole === 'srl') {
      base.push({ text: 'What is a motion to compel?', mode: 'external' });
    } else if (activeRole === 'pro') {
      base.push({ text: 'How do I deauth Clio integration?', mode: 'internal' });
    } else if (activeRole === 'admin') {
      base.push({ text: 'How do marketplace moderation tools work?', mode: 'internal' });
    }

    return base;
  }, [currentRoute, activeRole]);

  const handleQuickPrompt = (prompt: { text: string; mode: 'internal' | 'external' | 'hybrid' }) => {
    setSearchQuery(prompt.text);
    setResearchMode(prompt.mode);
  };

  return (
    <main className="min-h-screen">
      {/* Hero Section with Research Input */}
      <div className="bg-gradient-to-b from-brand-navy to-brand-navy/95 text-white py-16 px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Sparkles className="w-8 h-8 text-brand-orange" />
              <h1 className="text-4xl font-bold">Research and Support Center</h1>
            </div>
            <p className="text-blue-100 text-lg">
              Ask about ThreadLock features or explore legal concepts
            </p>
            {currentRoute && (
              <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm border border-white/20">
                <Info className="w-4 h-4" />
                Current page: <span className="font-semibold">{currentRoute.component.replace(/Page$/, '')}</span>
              </div>
            )}
          </div>

          {/* Primary Search Input */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full pl-12 pr-4 py-4 bg-slate-700 rounded-lg text-slate-100 text-lg placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange shadow-lg"
            />
          </div>

          {/* Research Mode Tabs */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => setResearchMode('internal')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                researchMode === 'internal'
                  ? 'bg-white text-brand-navy shadow-md'
                  : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              How ThreadLock Works
            </button>
            <button
              onClick={() => setResearchMode('external')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                researchMode === 'external'
                  ? 'bg-white text-brand-navy shadow-md'
                  : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
              }`}
            >
              <Globe className="w-4 h-4" />
              External Legal Info
            </button>
            <button
              onClick={() => setResearchMode('hybrid')}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                researchMode === 'hybrid'
                  ? 'bg-white text-brand-navy shadow-md'
                  : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              Both
            </button>
          </div>

          {/* Quick Prompts */}
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {quickPrompts.map((prompt, idx) => (
              <button
                key={idx}
                onClick={() => handleQuickPrompt(prompt)}
                className="px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-sm transition-colors"
              >
                {prompt.text}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-6xl mx-auto px-8 py-12">
        {/* AI Research Assistant */}
        <section className="mb-12">
          <AiResearchAssistant
            role={activeRole}
            searchQuery={searchQuery}
            researchMode={researchMode}
            currentRoute={currentRoute}
            routeCapabilities={routeCapabilities}
          />
        </section>

        {/* Contact Support Section */}
        <section className="mb-12">
          <div className="max-w-2xl mx-auto">
            <ContactSupportForm userRole="SRL" />
          </div>
        </section>

        {/* Contextual Information Panels */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Current Page Context (if on a feature page) */}
          {currentRoute && routeCapabilities.length > 0 && (
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-brand-orange" />
                <h3 className="font-semibold text-slate-50">About This Page</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-slate-400">Navigation:</p>
                  <p className="text-slate-200 font-medium">
                    {currentRoute.navReachability.replace('NAV_', '').replace('_', ' ')}
                  </p>
                </div>
                {routeCapabilities.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-400">Capabilities:</p>
                    <div className="space-y-1 mt-1">
                      {routeCapabilities.slice(0, 3).map((cap) => (
                        <div key={cap.key} className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-brand-orange rounded-full"></span>
                          <span className="text-sm text-slate-200">{cap.claim}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Core Features Available */}
          {!truthLoading && coreCapabilities.length > 0 && (
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <HelpCircle className="w-5 h-5 text-blue-500" />
                <h3 className="font-semibold text-slate-50">Key Features for {roleLabel[activeRole]}</h3>
              </div>
              <div className="space-y-2">
                {coreCapabilities.slice(0, 5).map((cap) => (
                  <button
                    key={cap.key}
                    onClick={() => {
                      setSearchQuery(`How does ${cap.claim.toLowerCase()} work?`);
                      setResearchMode('internal');
                    }}
                    className="w-full text-left px-3 py-2 rounded hover:bg-slate-700/50 transition-colors"
                  >
                    <p className="text-sm font-medium text-slate-200">{cap.claim}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {cap.routes.length} route{cap.routes.length !== 1 ? 's' : ''}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Knowledge Domains Legend */}
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-6">
          <h4 className="font-semibold text-slate-50 mb-4">Understanding Research Sources</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex gap-3">
              <BookOpen className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-slate-50">About ThreadLock (Internal)</p>
                <p className="text-sm text-slate-300 mt-1">
                  Sourced from ThreadLock Platform Documentation.
                  Cannot drift from reality.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Globe className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-slate-50">General Information (External)</p>
                <p className="text-sm text-slate-300 mt-1">
                  Educational content from external sources. Not legal advice.
                  Not ThreadLock-specific. For reference only.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
